import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BadgePlus, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useHasPermission } from '@/hooks/usePermissions';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { jobKeys, schedulerKeys } from '@/lib/queryKeys';
import { COLLAPSE_MS } from './collapse';

interface Props {
  /** Fired once the fix lands, so the row that reported it can animate out. */
  onResolved?: () => void;
  compact?: boolean;
  technicianId: string;
  technicianName?: string;
  serviceTypeId: string;
  serviceTypeName?: string;
}

export function AddMissingSkillButton(props: Props) {
  const canEdit = useHasPermission('technicians:edit');
  return canEdit ? (
    <SkillAssignmentButton key={`${props.technicianId}-${props.serviceTypeId}`} {...props} />
  ) : props.compact ? (
    <span className="rounded border border-current/20 px-2 py-1 text-xs">
      {props.serviceTypeName ?? 'Missing skill'}
    </span>
  ) : null;
}

function SkillAssignmentButton({
  technicianId,
  serviceTypeId,
  serviceTypeName,
  technicianName = 'technician',
  compact = false,
  onResolved,
}: Props) {
  const queryClient = useQueryClient();
  const add = useApiMutation<unknown>({
    mutationFn: () =>
      apiPost(operationsClient, schedulerEndpoints.technicianExpertise(technicianId), {
        serviceTypeId,
      }),
    onSuccess: async () => {
      toast.success('Skill added to technician');
      // The objection this row reported is no longer true. Let it collapse
      // first, THEN re-read: the server drops it from every open visit that
      // still records it, and refetching mid-animation would snap the row away
      // instead of playing it out.
      onResolved?.();
      await new Promise((resolve) => setTimeout(resolve, COLLAPSE_MS));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: schedulerKeys.all() }),
        queryClient.invalidateQueries({ queryKey: jobKeys.all() }),
      ]);
    },
  });

  return (
    <span className={compact ? 'inline-flex' : 'mt-2 block'}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-2 rounded-md border-current/20 bg-background/80 px-3 text-xs font-medium text-inherit shadow-none hover:bg-background"
        disabled={add.isPending || add.isSuccess}
        onClick={(event) => {
          event.stopPropagation();
          add.mutate();
        }}
      >
        {add.isPending ? (
          <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
        ) : add.isSuccess ? (
          <Check aria-hidden="true" className="size-3.5" />
        ) : (
          <BadgePlus aria-hidden="true" className="size-3.5" />
        )}
        {add.isPending
          ? 'Adding skill…'
          : add.isSuccess
            ? 'Skill added'
            : serviceTypeName
              ? `Add ${serviceTypeName} skill to ${technicianName}`
              : `Add missing skill to ${technicianName}`}
      </Button>
    </span>
  );
}
